import * as z from 'zod'

/**
 * Why a background generation (a mood sketch, a demo track) did not produce anything, written
 * onto the pending stub as `metadata.generationError` when the task finalises it (GH #1958).
 *
 * `reason` only — never the server's error text: the stub's metadata is copied into the Slate
 * document by `RefBlock`, where every collaborator can read it.
 */
export const generationErrorReasons = ['quota-exceeded', 'no-profile', 'model-error', 'timeout'] as const
export type GenerationErrorReason = (typeof generationErrorReasons)[number]

/** What a writer may persist. Strict, so a `message` can never sneak back in. */
export const generationErrorSchema = z.object({ reason: z.enum(generationErrorReasons) }).strict()
export type GenerationError = z.infer<typeof generationErrorSchema>

export function isGenerationErrorReason(value: unknown): value is GenerationErrorReason {
  return typeof value === 'string' && (generationErrorReasons as readonly string[]).includes(value)
}

/**
 * The lenient read side: any non-empty `reason` string counts as "generation failed", so a client
 * older than the reason it meets still shows the generic failed state instead of a spinner.
 */
export const getGenerationError = (
  entity: { metadata?: Record<string, unknown> | null } | null | undefined
): { reason: string } | undefined => {
  const marker = entity?.metadata?.generationError
  if (!marker || typeof marker !== 'object') {
    return undefined
  }
  const { reason } = marker as { reason?: unknown }
  return typeof reason === 'string' && reason.length > 0 ? { reason } : undefined
}
